/**
 * The public proof surface's own vocabulary and rules — `m09-public.js`, plus the four helpers it
 * reaches into neighbouring reference modules for.
 *
 * These live here rather than in `lib/reference.ts` because that file is shared and frozen for the
 * duration of the rebuild. Everything below is a straight port: the two-language string table the
 * page renders from, the fail-closed public-code lookup (`m03b-issuance.js:86`), the metadata
 * projection that decides which custom fields a visitor may see (`m02-mockdb.js:444`), the visual
 * class the card's artwork panel carries (`m08-admin.js:10`), and the single CTA decision engine
 * every surface is required to ask instead of deciding for itself (`m05b-linkedin-api.js:94`).
 */
import type { DemoCredential, DemoFieldDefinition, DemoSettings } from '../data/types';
import { withoutRegisteredMark } from './display-text';
import { accusativeTitleLower, titleWithDocumentNoun } from './document-noun';
import { type LinkedInIntegrationState, linkedInState } from './linkedin-integration';
import { credentialIssuingOrganization } from './linkedin-issuing-organization';
import {
  credentialViewState,
  credPermissions,
  credTitle,
  credType,
  displayName,
  fmtDate,
  publicUrl,
} from './reference';
import { resolveAssetRef, type Terms } from './render';

// ------------------------------------------------------------------------ the page's two languages

export interface PublicStrings {
  brandSub: string;
  /**
   * Shown instead of opening LinkedIn when the record cannot describe itself completely — a
   * missing title, issue date, public code or public page. Saying so beats opening a blank form.
   */
  liIncomplete: string;
  noteActive: (T: Terms) => string;
  noteExpired: (d: string) => string;
  noteRevoked: (d: string) => string;
  notePending: string;
  notePendingAdmin: string;
  pendingTitle: string;
  pendingBody: string;
  pendingBodyAdmin: string;
  holder: string;
  holderOrg: string;
  issuedBy: string;
  issueDate: string;
  expiration: string;
  noExpiry: string;
  credId: string;
  credUrl: string;
  copy: string;
  copied: string;
  updated: string;
  revokedAt: string;
  period: string;
  aboutBadge: string;
  aboutCert: string;
  aboutOrg: string;
  criteria: string;
  whatVerifies: string;
  disclaimer: string;
  issuerLinks: string;
  website: string;
  liPage: string;
  addProfile: string;
  addProfileNote: string;
  share: string;
  shareNote: string;
  shareOrgNote: string;
  print: string;
  copyUrl: string;
  linkCopied: string;
  ogPreview: string;
  published: string;
  /**
   * Shown when the language on screen has no sealed document of its own.
   *
   * The alternative was showing the other language's certificate under this language's name, which
   * is the one thing the TR/EN round exists to prevent. So the page says what it has instead.
   */
  noDocumentInLanguage: string;
  /**
   * The address asked for a language this page does not have.
   *
   * Named rather than swallowed, and never resolved to «Turkish, near enough»: the visitor is told
   * what they asked for and which language they are actually reading.
   */
  unsupportedLang: (asked: string, showing: string) => string;
  revokedHint: string;
  superseded: string;
  supersededSame: string;
  expiredHint: string;
  notFound: string;
  notFoundNote: string;
  ambiguousTitle: string;
  ambiguousNote: string;
  ambiguousCode: string;
  lookup: string;
  lookupPh: string;
  verifyTitle: string;
  verifyIntro: string;
  foot: string;
  proto: string;
}

export type PublicLanguage = 'tr' | 'en';

export const PUBLIC_I18N: Record<PublicLanguage, PublicStrings> = {
  tr: {
    brandSub: 'Sertifika doğrulama',
    liIncomplete:
      'Bu kayıt LinkedIn formunu dolduracak bilgileri taşımıyor; boş form açmak yerine işlem durduruldu.',
    noteActive: (T) => `Bu ${T.nounDef} aktif ve Happy Place to Work kayıt defterinde yer alıyor.`,
    noteExpired: (d) => `Geçerlilik ${d} tarihinde sona erdi; kayıt doğrulama amacıyla yayında kalır.`,
    noteRevoked: (d) =>
      `Bu kayıt ${d} tarihinde Happy Place to Work tarafından iptal edildi ve artık geçerli değil.`,
    notePending: 'Bu kayıt henüz alıcısı tarafından yayınlanmadı.',
    notePendingAdmin: 'Bu kayıt henüz veren kurum tarafından yayınlanmadı.',
    pendingTitle: 'Onay bekleyen kayıt',
    pendingBody:
      'Kayıt alıcısı onaylayana kadar kişisel bilgiler ve kişiselleştirilmiş görsel bu sayfada gösterilmez. Onaydan sonra sayfa aynı adreste yayınlanır.',
    pendingBodyAdmin:
      'Veren kurum yayın onayı verene kadar kişisel bilgiler ve kişiselleştirilmiş görsel bu sayfada gösterilmez. Onaydan sonra sayfa aynı adreste yayınlanır.',
    holder: 'Sahibi',
    holderOrg: 'Sertifika sahibi kuruluş',
    issuedBy: 'Veren kurum',
    issueDate: 'Veriliş tarihi',
    expiration: 'Geçerlilik tarihi',
    noExpiry: 'Süresiz',
    credId: 'Sertifika ID',
    credUrl: 'Sertifika URL',
    copy: 'Kopyala',
    copied: 'Kopyalandı',
    updated: 'Kayıt son güncelleme',
    revokedAt: 'İptal tarihi',
    period: 'Sertifikasyon dönemi',
    aboutBadge: 'Bu rozet nedir?',
    aboutCert: 'Bu sertifika nedir?',
    aboutOrg: 'Bu şirket sertifikası nedir?',
    criteria: 'Verilme kriteri',
    whatVerifies: 'Bu kayıt neyi doğruluyor?',
    disclaimer: 'Açıklama metinleri bir değerlendirme sonucu ya da bilimsel kanıt iddiası taşımaz.',
    issuerLinks: 'Veren kurum',
    website: 'Web sitesi',
    liPage: 'LinkedIn Page',
    addProfile: 'LinkedIn Profiline Ekle',
    addProfileNote:
      "LinkedIn formu açılır. Bilgileri yardımcımızla hızlıca kopyalayıp LinkedIn'de Kaydet'e basarsınız — LinkedIn alanları otomatik doldurulmaz.",
    share: "LinkedIn'de Gönderi Olarak Paylaş",
    shareNote:
      'Rozet veya sertifika görselini LinkedIn akışınızda duyurur; profilinizin Lisanslar ve Sertifikalar bölümüne eklemez.',
    shareOrgNote:
      "Şirket sayfasından paylaşım LinkedIn'de sayfa yöneticisinin yetkisine bağlıdır; otomatik yayın yapılmaz.",
    print: 'PDF olarak Kaydet / Yazdır',
    copyUrl: 'Bağlantıyı kopyala',
    linkCopied: 'Sertifika bağlantısı kopyalandı',
    ogPreview: 'Paylaşım önizlemesi',
    published: 'Kaydınız yayınlandı — bu sayfa artık herkese açık.',
    noDocumentInLanguage:
      'Bu sertifikanın Türkçe sürümü bulunmuyor. Kayıt geçerlidir; belgeyi kendi dilinde görmek için dili değiştirin.',
    unsupportedLang: (asked, showing) =>
      `Adreste istenen dil («${asked}») bu sayfada yok. Sayfa ${showing} gösteriliyor; başka bir dilin belgesi bu dilin adı altında gösterilmez.`,
    revokedHint:
      "İptal edilmiş bir kayıt LinkedIn'e eklenemez, paylaşılamaz ve indirilemez; sayfa doğrulama kaydı olarak yayında kalır.",
    superseded: 'Bu kaydın yerine yeni bir kayıt düzenlenmiştir.',
    supersededSame: 'Yeni kaydınızı görüntüleyin',
    expiredHint: "Süresi dolmuş kayıt indirilebilir; LinkedIn'e eklerken bitiş tarihini belirtin.",
    notFound: 'Sertifika bulunamadı',
    notFoundNote:
      'Bu kodla eşleşen bir kayıt yok. Kodu kontrol edip yeniden deneyin; kod büyük/küçük harfe duyarlı değildir.',
    ambiguousTitle: 'Kod tek bir kayda çözülemedi',
    ambiguousNote:
      'Girdiğiniz kod birden fazla kayda karşılık geliyor, bu yüzden hiçbiri gösterilmedi. Kodu belgedeki hâliyle, tam olarak yazın. Sorun sürerse belgeyi düzenleyen kurumla iletişime geçin.',
    ambiguousCode: 'Referans: PUBLIC_CODE_AMBIGUOUS',
    lookup: 'Doğrula',
    lookupPh: 'HPW-XXXX-XXXX-XXXX-XXXX-XXXX',
    verifyTitle: 'Sertifika doğrulama',
    verifyIntro:
      "Rozet ya da sertifika üzerindeki Sertifika ID'yi girin. İsim, e-posta veya şirket adına göre arama yoktur.",
    foot: 'Happy Place to Work · Bu sayfa arama motorlarında dizinlenmez (noindex) · Bağlantıyı bilen herkes doğrulayabilir.',
    proto: 'PROTOTİP — Bu doğrulama kaydı örnek veridir.',
  },
  en: {
    brandSub: 'Credential verification',
    liIncomplete:
      'This record does not carry the values the LinkedIn form needs, so it was not opened empty.',
    noteActive: (T) =>
      `This ${T.nounDef} is active and on record in the Happy Place to Work issuer registry.`,
    noteExpired: (d) => `Validity ended on ${d}; the record stays online for verification.`,
    noteRevoked: (d) => `This record was revoked by Happy Place to Work on ${d} and is no longer valid.`,
    notePending: 'This credential has not yet been published by its recipient.',
    notePendingAdmin: 'This credential has not yet been published by the issuer.',
    pendingTitle: 'Record awaiting acceptance',
    pendingBody:
      'Personal details and the personalised image are not shown until the recipient accepts. After acceptance the page is published at this same address.',
    pendingBodyAdmin:
      'Personal details and the personalised image are not shown until the issuer publishes the record. After publication the page is served at this same address.',
    holder: 'Awarded to',
    holderOrg: 'Certified organization',
    issuedBy: 'Issued by',
    issueDate: 'Issue date',
    expiration: 'Expiration date',
    noExpiry: 'No expiration',
    /* English keeps the English word, spelled the English way — and the surface declares `lang="en"`,
       so CSS folds it to CREDENTIAL rather than the Turkish CREDENTİAL that was reported. */
    credId: 'Credential ID',
    credUrl: 'Credential URL',
    copy: 'Copy',
    copied: 'Copied',
    updated: 'Record last updated',
    revokedAt: 'Revoked on',
    period: 'Certification period',
    aboutBadge: 'What is this badge?',
    aboutCert: 'What is this certificate?',
    aboutOrg: 'What is this company certificate?',
    criteria: 'Issuing criteria',
    whatVerifies: 'What does this record verify?',
    disclaimer: 'Descriptions are not assessment results and make no scientific claim.',
    issuerLinks: 'Issuer',
    website: 'Website',
    liPage: 'LinkedIn Page',
    addProfile: 'Add to LinkedIn Profile',
    addProfileNote:
      'The LinkedIn form opens. You copy the details with our helper and press Save on LinkedIn — LinkedIn does not pre-fill the fields.',
    share: 'Share as a LinkedIn Post',
    shareNote:
      'Announces the badge or certificate image in your LinkedIn feed; it does not add an entry to the Licenses & certifications section of your profile.',
    shareOrgNote:
      "Sharing from a company page depends on the LinkedIn page admin's permissions; nothing is posted automatically.",
    print: 'Save as PDF / Print',
    copyUrl: 'Copy link',
    linkCopied: 'Credential link copied',
    ogPreview: 'Share preview',
    published: 'Your record is published — this page is now public.',
    noDocumentInLanguage:
      'An English version of this certificate is not available. The record is valid; switch the language to see the document in the language it was issued in.',
    unsupportedLang: (asked, showing) =>
      `The language the address asked for («${asked}») is not available on this page. It is showing ${showing}; another language's document is never shown under this language's name.`,
    revokedHint:
      'A revoked record cannot be added to LinkedIn, shared or downloaded; the page stays online as a verification record.',
    superseded: 'A new record has been issued in place of this credential.',
    supersededSame: 'View your new record',
    expiredHint:
      'An expired record can still be downloaded; state the expiration date when adding it to LinkedIn.',
    notFound: 'Credential not found',
    notFoundNote: 'No record matches this code. Check the code and try again; codes are not case-sensitive.',
    ambiguousTitle: 'Code did not resolve to a single record',
    ambiguousNote:
      'The code you entered matches more than one record, so none was shown. Please type the code exactly as printed. If the problem persists, contact the issuing organisation.',
    ambiguousCode: 'Reference: PUBLIC_CODE_AMBIGUOUS',
    lookup: 'Verify',
    lookupPh: 'HPW-XXXX-XXXX-XXXX-XXXX-XXXX',
    verifyTitle: 'Credential verification',
    verifyIntro:
      'Enter the Credential ID printed on the badge or certificate. There is no search by name, e-mail or company.',
    foot: 'Happy Place to Work · This page is not indexed by search engines (noindex) · Anyone with the link can verify it.',
    proto: 'PROTOTYPE — This verification record is sample data.',
  },
};

// -------------------------------------------------------------------------- fail-closed code lookup

/**
 * `canonicalPublicCodeKey` — `m03b-issuance.js:51`.
 *
 * The confusable characters collapse (I and L to 1, O to 0, U to V) so a code read off a printed
 * certificate still resolves. That collapsing is exactly why the lookup below has to fail closed:
 * two different codes can share one key.
 */
export function canonicalPublicCodeKey(input: string | null | undefined): string {
  let s = String(input ?? '')
    .toUpperCase()
    .replace(/[^0-9A-Z]/g, '');
  s = s.replace(/^HPW/, '');
  return s.replace(/[IL]/g, '1').replace(/O/g, '0').replace(/U/g, 'V');
}

export type PublicLookup =
  | { ok: true; credential: DemoCredential; exact: boolean }
  | { ok: false; code: 'not_found' }
  | { ok: false; code: 'public_code_ambiguous_collision'; count: number };

/**
 * `lookupByPublicCode` — `m03b-issuance.js:86`.
 *
 * A key that resolves to more than one record shows NOTHING. Picking the first hit would publish
 * one person's record under another person's code, so the page answers with §13's own message and
 * leaks neither the count of matches nor any of their codes.
 */
export function lookupByPublicCode(input: string | null | undefined, pool: DemoCredential[]): PublicLookup {
  const raw = String(input ?? '')
    .trim()
    .toUpperCase();
  const exact = pool.find((c) => c.publicCode === raw);
  if (exact) return { ok: true, credential: exact, exact: true };
  const key = canonicalPublicCodeKey(raw);
  if (!key) return { ok: false, code: 'not_found' };
  const hits = pool.filter((c) => canonicalPublicCodeKey(c.publicCode) === key);
  const first = hits[0];
  if (hits.length === 1 && first) return { ok: true, credential: first, exact: false };
  if (hits.length > 1) return { ok: false, code: 'public_code_ambiguous_collision', count: hits.length };
  return { ok: false, code: 'not_found' };
}

// ---------------------------------------------------------------------------- what a visitor sees

const canPublicMetadata = (v: string): boolean =>
  v === 'public_metadata' || v === 'public_metadata_and_render';

export interface PublicField {
  key: string;
  label: string;
  value: string;
}

/** `fieldLabel` — `m02-mockdb.js`; the key is the last resort, never an empty `<dt>`. */
const labelOf = (f: DemoFieldDefinition, lang: PublicLanguage): string =>
  (f.labels ? (lang === 'en' ? f.labels.en : f.labels.tr) : null) || f.key;

/**
 * `publicMetadataFields` — `m02-mockdb.js:444`, read through the credential's own issuance release.
 *
 * A `private` field never reaches this list and neither does an `auto` one; the holder field is
 * dropped because the card already prints it above. Empty values are filtered out rather than
 * rendered as a blank row, which is what keeps the definition list the same height as the
 * reference's for the same record.
 */
export function credPublicFields(c: DemoCredential, lang: PublicLanguage): PublicField[] {
  const claims = c.claims;
  const holderField = c.release.holderField;
  const out: PublicField[] = [];
  for (const f of c.release.fieldDefinitions ?? []) {
    if (f.auto || !canPublicMetadata(f.visibility) || f.key === holderField) continue;
    const raw = claims[f.key];
    if (!raw) continue;
    out.push({ key: f.key, label: labelOf(f, lang), value: String(raw) });
  }
  return out;
}

/**
 * `credVisualClass` — `m08-admin.js:10`.
 *
 * An artwork with alpha is drawn over the panel's own ground and keeps its 480px cap; an opaque
 * one is a certificate sheet and gets the full panel width. The class is decided from the resolved
 * asset, not from the layout, so a background whose digest no longer matches falls back to `cert`.
 */
export function credVisualClass(c: DemoCredential): string {
  const bg = c.release.background as {
    assetRef?: { assetId?: string; sha256?: string };
    hasAlpha?: boolean;
  } | null;
  /*
   * The release's own recorded answer wins where it has one.
   *
   * A visitor on `/c/:code` never loads the asset library — the page is anonymous and the bytes
   * are not theirs to fetch — so `resolveAssetRef` would answer `not_found` there and every badge
   * would be drawn at certificate width. The editor records `hasAlpha` with the background at the
   * moment it chooses it (`EditorBackground.hasAlpha`) and the publish gate freezes it into the
   * release, so the public page can read the same fact the designer saw. A release published
   * before that field existed carries no answer and falls through to the resolver below, which is
   * exactly the previous behaviour.
   */
  if (typeof bg?.hasAlpha === 'boolean') return bg.hasAlpha ? '' : 'cert';
  const r = bg?.assetRef ? resolveAssetRef(bg.assetRef) : null;
  return r?.ok && r.asset.hasAlpha ? '' : 'cert';
}

// --------------------------------------------------------------------------- the one CTA decision

export const linkedInShareUrl = (url: string): string =>
  `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;

export interface ProfileAddCTA {
  key: 'api_add' | 'api_connect' | 'static_add';
  label: string;
  flow: 'api_preconfirm' | 'api_authorize' | 'static_helper';
  note: string;
}

export interface FeedShareCTA {
  key: 'feed_share';
  label: string;
  note: string;
  url: string;
}

export interface LinkedInCTA {
  profileAdd: ProfileAddCTA | null;
  feedShare: FeedShareCTA | null;
  download: boolean;
  copyLink: boolean;
  mode: string;
  reason: string | null;
  simulationBadge: string | null;
}

const SIMULATION_LABEL =
  'PROTOTİP SİMÜLASYONU — LinkedIn tarafından onaylanmış Profile Edit API erişimi ve üye yetkilendirmesi gerektirir.';

/**
 * `resolveLinkedInCTA` — `m05b-linkedin-api.js:94`, §20/D.
 *
 * Every surface asks this and none of them decides for itself, which is the only reason binding
 * rules 13–15 hold: an organization credential can never produce a profile-add CTA, a revoked or
 * superseded record produces none at all, and the honest static helper is what a visitor gets
 * until LinkedIn has actually approved Profile Edit API access.
 */
export function resolveLinkedInCTA(
  c: DemoCredential,
  integrationState?: LinkedInIntegrationState,
): LinkedInCTA {
  const st = integrationState ?? linkedInState();
  const isOrg = c.release.subjectType === 'organization';
  const perms = credPermissions(c);
  const view = credentialViewState(c);
  const out: LinkedInCTA = {
    profileAdd: null,
    feedShare: null,
    download: Boolean(perms.allowDownload) && view !== 'revoked',
    copyLink: true,
    mode: st.integrationMode,
    reason: null,
    simulationBadge: null,
  };

  /* A superseded record is no longer the current version: adding THAT one to a profile would
     publish something the issuer has already replaced. The successor carries its own CTA. */
  if (view === 'revoked' || view === 'superseded') {
    out.reason = view;
    out.download = false;
    return out;
  }
  if (c.state === 'pending_acceptance' || c.state === 'pending_admin_publish' || c.state === 'declined') {
    out.reason = 'not_active_yet';
    return out;
  }

  if (perms.allowLinkedInShare) {
    out.feedShare = {
      key: 'feed_share',
      label: "LinkedIn'de gönderi olarak paylaş",
      note: 'Bu işlem kaydı LinkedIn akışınızda paylaşır; profilinizin Lisanslar ve Sertifikalar bölümüne eklemez.',
      url: linkedInShareUrl(publicUrl(c)),
    };
  }

  if (isOrg) {
    out.reason = 'organization_subject';
    return out;
  }
  if (!perms.allowLinkedInProfile) {
    out.reason = 'permission_off';
    return out;
  }

  if (st.apiAccessStatus === 'approved' && st.memberConnectionStatus === 'connected') {
    out.profileAdd = {
      key: 'api_add',
      label: 'LinkedIn Profilime Ekle',
      flow: 'api_preconfirm',
      note: 'Bilgiler sertifika kaydından okunur; onayınızla LinkedIn profilinize eklenir.',
    };
    out.simulationBadge = st.simulated ? SIMULATION_LABEL : null;
    out.mode = 'profile_edit_api';
    return out;
  }
  if (st.apiAccessStatus === 'approved') {
    out.profileAdd = {
      key: 'api_connect',
      label: 'LinkedIn ile Bağlan ve Profilime Ekle',
      flow: 'api_authorize',
      note: 'Önce LinkedIn hesabınıza bağlanır, sonra ön onay ekranı açılır.',
    };
    out.simulationBadge = st.simulated ? SIMULATION_LABEL : null;
    out.mode = 'profile_edit_api';
    return out;
  }
  out.profileAdd = {
    key: 'static_add',
    label: "LinkedIn'e Hızlı Ekle",
    flow: 'static_helper',
    note: "LinkedIn formu açılır. Bilgileri HPW yardımcısıyla hızlıca kopyalayıp LinkedIn'de Kaydet'e basarsınız.",
  };
  return out;
}

// ------------------------------------------------------------------- the copy-it-yourself helper

export interface HelperStrings {
  title: string;
  eyebrow: string;
  intro: string;
  blocked: string;
  reopen: string;
  open: string;
  progress: (a: number, b: number) => string;
  copy: string;
  copied: string;
  copyNext: string;
  fields: Record<string, string>;
  noExpiry: string;
  expiredWarn: string;
  all: string;
  allCopied: string;
  restart: string;
  saved: string;
  close: string;
  savedToast: string;
  /**
   * The «Veren kuruluş» guidance, in terms of the organisation THIS record names.
   *
   * A function rather than a sentence because the organisation is a per-template setting now:
   * a certificate issued on a customer's behalf must not tell the holder to type Happy Place to
   * Work into the LinkedIn form.
   */
  issuerWarn: (organizationName: string) => string;
  /** Said instead, when the record's stored organisation cannot be read at all. */
  issuerUnreadable: string;
  /** The «Veren kuruluş» value in the copy list when it cannot be read. */
  issuerUnknown: string;
  openPage: string;
  copyIssuer: string;
  firstCopied: string;
  firstCopyFailed: string;
  howItWorks: string;
  mockPreview: string;
  previewTitle: string;
  previewIssued: string;
  previewExp: string;
  previewNoExp: string;
  previewShow: string;
  previewNote: string;
  allText: Record<string, string>;
}

export const HELPER_I18N: Record<PublicLanguage, HelperStrings> = {
  tr: {
    title: 'LinkedIn profilinize ekleyin',
    eyebrow: 'LinkedIn · Lisanslar ve Sertifikalar',
    intro:
      "LinkedIn, sertifika alanlarını üçüncü taraflar için otomatik doldurmaz. Form yeni sekmede açıldı; aşağıdaki alanları sırayla kopyalayıp LinkedIn formuna yapıştırın ve LinkedIn tarafında Kaydet'e basın.",
    blocked: 'Tarayıcı yeni sekmeyi engelledi. LinkedIn formunu aşağıdaki düğmeyle açabilirsiniz.',
    reopen: 'LinkedIn formunu yeniden aç ↗',
    open: 'LinkedIn formunu aç ↗',
    progress: (a, b) => `${a}/${b} alan kopyalandı`,
    copy: 'Kopyala',
    copied: 'Kopyalandı ✓',
    copyNext: 'Kopyala ve sonraki alana geç',
    fields: {
      name: 'Sertifika adı',
      org: 'Veren kuruluş',
      im: 'Veriliş ayı',
      iy: 'Veriliş yılı',
      em: 'Bitiş ayı',
      ey: 'Bitiş yılı',
      id: 'Credential ID',
      url: 'Credential URL',
    },
    noExpiry:
      "Bu kaydın bitiş tarihi yok: LinkedIn formunda «Bu credential'ın süresi dolmaz» seçeneğini işaretleyin.",
    expiredWarn: "Bu kaydın süresi dolmuştur. LinkedIn'de bitiş tarihini doğru biçimde belirtin.",
    all: 'Tüm bilgileri kopyala',
    allCopied: 'Tüm bilgiler kopyalandı',
    restart: 'Baştan başla',
    saved: 'Kaydettim',
    close: 'Kapat',
    savedToast: "Teşekkürler — LinkedIn'de kaydettiğinizi not ettik.",
    issuerWarn: (organizationName: string) =>
      `Veren kuruluş alanına «${organizationName}» yazın ve açılan önerilerden resmî LinkedIn Page'i seçin. Yalnız metni yazıp Page'i seçmezseniz kurum logosu ve Page bağlantısı görünmeyebilir.`,
    issuerUnreadable:
      'Bu kaydın veren kuruluş bilgisi okunamadı; LinkedIn profiline ekleme bu kayıt için kullanılamıyor. Sertifikanın doğrulama sayfası ve indirme bundan etkilenmez.',
    issuerUnknown: 'Okunamadı',
    openPage: "Resmî LinkedIn Page'i aç ↗",
    copyIssuer: 'Kuruluş adını kopyala',
    firstCopied: 'Sertifika adı panoya kopyalandı — LinkedIn formundaki ilk alana yapıştırabilirsiniz.',
    firstCopyFailed: 'Pano izni verilmedi; alanları aşağıdaki düğmelerle tek tek kopyalayabilirsiniz.',
    howItWorks:
      "LinkedIn formu açılır. Bilgileri yardımcımızla hızlıca kopyalayıp LinkedIn'de Kaydet'e basarsınız.",
    mockPreview: 'Temsili LinkedIn önizlemesi — gerçek Page logosu yüklenmedi.',
    previewTitle: 'LinkedIn profilinde nasıl görünür?',
    previewIssued: 'Verildi',
    previewExp: 'Bitiş',
    previewNoExp: 'Süresiz',
    previewShow: "Credential'ı göster",
    previewNote:
      "LinkedIn'in Lisanslar ve Sertifikalar bölümünde rozet veya sertifika görselinin kendisi yerine veren kuruluşun LinkedIn Page logosu gösterilir. «Credential'ı göster» bağlantısı tıklandığında kişiye özel HPW doğrulama sayfası açılır.",
    allText: {
      name: 'Sertifika adı',
      org: 'Veren kuruluş',
      im: 'Veriliş ayı',
      iy: 'Veriliş yılı',
      em: 'Bitiş ayı',
      ey: 'Bitiş yılı',
      id: 'Credential ID',
      url: 'Credential URL',
      noexp: "Bitiş: süresiz (LinkedIn'de «süresi dolmaz» seçeneği)",
    },
  },
  en: {
    title: 'Add to your LinkedIn profile',
    eyebrow: 'LinkedIn · Licenses & certifications',
    intro:
      'LinkedIn does not pre-fill certification fields for third parties. The form opened in a new tab; copy the fields below in order, paste them into the LinkedIn form and press Save on LinkedIn.',
    blocked: 'Your browser blocked the new tab. Open the LinkedIn form with the button below.',
    reopen: 'Reopen LinkedIn form ↗',
    open: 'Open LinkedIn form ↗',
    progress: (a, b) => `${a}/${b} fields copied`,
    copy: 'Copy',
    copied: 'Copied ✓',
    copyNext: 'Copy and move to the next field',
    fields: {
      name: 'Name',
      org: 'Issuing organization',
      im: 'Issue month',
      iy: 'Issue year',
      em: 'Expiration month',
      ey: 'Expiration year',
      id: 'Credential ID',
      url: 'Credential URL',
    },
    noExpiry:
      'This record has no expiration date: tick “This credential does not expire” on the LinkedIn form.',
    expiredWarn: 'This credential has expired. State the expiration date correctly on LinkedIn.',
    all: 'Copy all details',
    allCopied: 'All details copied',
    restart: 'Start over',
    saved: 'I saved it',
    close: 'Close',
    savedToast: 'Thanks — we noted that you saved it on LinkedIn.',
    issuerWarn: (organizationName: string) =>
      `Type “${organizationName}” into the Issuing organization field and pick the official LinkedIn Page from the suggestions. If you only type the text without selecting the Page, the organisation logo and Page link may not appear.`,
    issuerUnreadable:
      "This record's issuing organisation could not be read, so adding it to a LinkedIn profile is unavailable for this record. The verification page and the download are unaffected.",
    issuerUnknown: 'Could not be read',
    openPage: 'Open the official LinkedIn Page ↗',
    copyIssuer: 'Copy organisation name',
    firstCopied: 'Certification name copied — paste it into the first field of the LinkedIn form.',
    firstCopyFailed: 'Clipboard permission was denied; copy each field with the buttons below.',
    howItWorks: 'The LinkedIn form opens. You copy the details with our helper and press Save on LinkedIn.',
    mockPreview: 'Representative LinkedIn preview — the real Page logo is not loaded.',
    previewTitle: 'How it appears on your LinkedIn profile',
    previewIssued: 'Issued',
    previewExp: 'Expires',
    previewNoExp: 'No expiration',
    previewShow: 'Show credential',
    previewNote:
      "In LinkedIn's Licenses & certifications section the issuing organization's LinkedIn Page logo is shown instead of the badge or certificate image itself. The “Show credential” link opens the personalised HPW verification page.",
    allText: {
      name: 'Name',
      org: 'Issuing organization',
      im: 'Issue month',
      iy: 'Issue year',
      em: 'Expiration month',
      ey: 'Expiration year',
      id: 'Credential ID',
      url: 'Credential URL',
      noexp: 'Expiration: none (tick “does not expire” on LinkedIn)',
    },
  },
};

export interface HelperField {
  key: string;
  label: string;
  value: string;
}

/**
 * `linkedInFields` — `m05-linkedin.js:29`.
 *
 * The certification NAME stays in the credential's own Turkish title even on the English page: it
 * is the title the issuer published, not a label to translate, and a visitor pasting a translated
 * name into LinkedIn would create a record that does not match the proof page.
 */
/** The «Veren kuruluş» row's value: the resolved organisation, or an honest «could not be read». */
function organizationRowValue(c: DemoCredential, lang: PublicLanguage, settings: DemoSettings): string {
  const resolved = credentialIssuingOrganization(c.release, settings);
  return resolved.source === 'unreadable'
    ? (HELPER_I18N[lang].issuerUnknown ?? '')
    : resolved.organizationName;
}

export function linkedInFields(
  c: DemoCredential,
  lang: PublicLanguage,
  settings: DemoSettings,
): HelperField[] {
  const L = HELPER_I18N[lang];
  const f: HelperField[] = [
    { key: 'name', label: L.fields.name ?? 'name', value: credTitle(c) },
    {
      key: 'org',
      label: L.fields.org ?? 'org',
      /* One resolver for the helper and the Add-to-Profile link alike: the template's own
         organisation when it set one, the record's issuer snapshot otherwise — and no name at
         all, rather than the issuer's, when the record's own setting cannot be read. */
      value: organizationRowValue(c, lang, settings),
    },
    { key: 'im', label: L.fields.im ?? 'im', value: fmtDate(c.issueDate, lang, 'monthName') },
    { key: 'iy', label: L.fields.iy ?? 'iy', value: fmtDate(c.issueDate, lang, 'year') },
  ];
  if (c.expiresAt) {
    f.push(
      { key: 'em', label: L.fields.em ?? 'em', value: fmtDate(c.expiresAt, lang, 'monthName') },
      { key: 'ey', label: L.fields.ey ?? 'ey', value: fmtDate(c.expiresAt, lang, 'year') },
    );
  }
  f.push(
    { key: 'id', label: L.fields.id ?? 'id', value: c.publicCode },
    { key: 'url', label: L.fields.url ?? 'url', value: publicUrl(c) },
  );
  return f;
}

/** `linkedInAllText` — the «süresiz» line is spliced in where the two expiry rows would have been. */
export function linkedInAllText(c: DemoCredential, lang: PublicLanguage, settings: DemoSettings): string {
  const L = HELPER_I18N[lang];
  const lines = linkedInFields(c, lang, settings).map((f) => `${L.allText[f.key] ?? f.label}: ${f.value}`);
  if (!c.expiresAt) lines.splice(4, 0, L.allText.noexp ?? '');
  return lines.join('\n');
}

// ----------------------------------------------------------------------------- the share surface

export interface ShareStrings {
  title: string;
  note: string;
  /**
   * Shown ONLY where this deployment has no LinkedIn integration, because there the single thing a
   * sharer needs to know before they click through is that LinkedIn does not accept a post body
   * from another site: the composer opens empty and the text has to be pasted by hand. The panel
   * says it rather than letting the empty box look like a bug — and it is why «Metni kopyala» sits
   * beside the button instead of behind it.
   *
   * Where the integration IS configured the product publishes the text itself, so this sentence
   * would describe a step that no longer exists and is not rendered at all.
   */
  noPrefill: string;
  orgNote: string;
  blocked: string;
  /**
   * The manual fallback, and only that: it opens LinkedIn's own composer with the page address and
   * nothing else. It is offered where the integration is not configured; where it is, the dialog
   * publishes the post itself and this second, different-looking way of sharing is not shown.
   */
  open: string;
  suggested: string;
  copyText: string;
  copyLink: string;
  png: string;
  preview: string;
  previewNote: string;
  /**
   * The three states of the server-rendered card. They exist because the picture is fetched, and a
   * fetch can be slow or fail; a dialog that showed a broken frame and said nothing is the defect
   * these replace.
   */
  previewLoading: string;
  previewFailed: string;
  /** Said when the LinkedIn status could not be established — not the same as «not configured». */
  statusUnavailable: string;
  statusRetry: string;
  /** Marks the link share as a different outcome when the publish flow could not be reached. */
  linkAlternative: string;
  /**
   * Said where the deployment itself answered that direct publishing is not configured.
   *
   * `linkAlternative` cannot carry this case: it says «instead», and in this state there is no
   * publish block above it for «instead» to refer to — the member is simply handed a link share
   * where an image post was expected, with nothing said. That silence is the whole of what was
   * reported, so the state names itself here. It names no variable and no provider: a member is
   * owed the outcome, not the deployment's configuration.
   */
  linkOnlyDeployment: string;
  previewRetry: string;
  /**
   * The member feed-post flow. These appear only where the deployment really has the LinkedIn
   * integration configured; where it does not, none of this is rendered at all, so a missing
   * configuration cannot look like a working automatic share.
   */
  postHeading: string;
  postNote: string;
  connect: string;
  connecting: string;
  connectedAs: string;
  /**
   * The image and text step, and the confirmation step after it.
   *
   * The dialog is one window with two steps, and these name them. `imageLabel` replaces the link-card
   * label on this path: the reader is looking at the picture their post will carry, not at metadata,
   * so nothing here says «OG» or «server-side». `editHint` sits beside the box it describes.
   */
  imageLabel: string;
  editHint: string;
  /**
   * What the button above it is for, said where a member who has not connected is looking.
   *
   * Rendered only while a connection is actually needed: leaving it under a connected account
   * would be instructing someone to do something they have already done.
   */
  connectNeeded: string;
  /**
   * Why the post will be longer than the box, shown ONLY when it will be.
   *
   * The server appends the record's verification address when the member has not written it — the
   * one line a reader of the post actually needs, and the rule stays. There is no separate
   * confirmation page any more, so the dialog owes the member the finished text HERE, beside the
   * box, and only in the case where the two differ. The default text already carries the address,
   * so in the ordinary case the box IS the post and nothing extra is shown.
   */
  finalTextNote: string;
  finalTextLabel: string;
  linkHint: string;
  /**
   * The three ways a text cannot be posted, said in the member's own words rather than as a code.
   * They are decided by the same rule the server uses, so a text the dialog accepts is a text the
   * server accepts.
   */
  textEmpty: string;
  textTooLong: string;
  textControl: string;
  /** Said once, immediately above the control that publishes: this post is public. */
  publicNotice: string;
  publish: string;
  publishing: string;
  published: string;
  viewPost: string;
  errDenied: string;
  errPopup: string;
  errExpired: string;
  /**
   * The API would not accept the start navigation — said as what it is, not as an expiry.
   *
   * Names no header and blames nobody: what the member can act on is that the attempt did not
   * leave, that nothing was shared, and that pressing again starts a clean one.
   */
  errStartRefused: string;
  errBindingUnverified: string;
  errTimedOut: string;
  errUnknown: string;
  /** Nothing was sent to LinkedIn and nothing was created; the same press will do. */
  errNotStarted: string;
  errFailed: string;
  close: string;
}

export const SHARE_I18N: Record<PublicLanguage, ShareStrings> = {
  tr: {
    title: "LinkedIn'de gönderi olarak paylaş",
    note: 'Rozet veya sertifika görselini LinkedIn akışınızda duyurur; profilinizin Lisanslar ve Sertifikalar bölümüne eklemez. Profil sertifikası için «LinkedIn Profiline Ekle» kullanın.',
    noPrefill: 'LinkedIn paylaşım metnini otomatik doldurmaz; metni kopyalayıp paylaşım kutusuna yapıştırın.',
    orgNote:
      "Şirket sayfasından paylaşım, LinkedIn'de şirket sayfası yöneticisinin yetkisine bağlıdır; bu sistem şirket sayfasında otomatik yayın yapmaz.",
    blocked: 'Tarayıcı paylaşım penceresini engelledi. Aşağıdaki seçenekleri kullanın.',
    open: "LinkedIn'de yalnızca bağlantıyı paylaş ↗",
    suggested: 'Önerilen paylaşım metni',
    copyText: 'Metni kopyala',
    copyLink: 'Bağlantıyı kopyala',
    png: 'PNG indir',
    preview: 'Bağlantı önizlemesi (OG görseli)',
    previewNote:
      "LinkedIn bağlantı kartı bu görseli ve başlığı kullanır; üretimde public route bu metadata'yı ilk HTML cevabında server-side üretir.",
    previewLoading: 'Paylaşım görseli yükleniyor…',
    previewFailed: 'Paylaşım görseli yüklenemedi.',
    statusUnavailable:
      'LinkedIn yayımlama durumu şu anda okunamadı; entegrasyonun kapalı olduğu anlamına gelmez. Yeniden deneyin.',
    statusRetry: 'Durumu yeniden sorgula',
    linkAlternative: 'Bunun yerine yalnız bağlantıyı paylaşabilirsiniz — bu, görselli gönderi yayımlamaz.',
    linkOnlyDeployment:
      "Bu dağıtımda LinkedIn'de doğrudan görselli gönderi paylaşımı şu anda açık değil. Aşağıdaki bağlantı paylaşımı LinkedIn'in kendi penceresini açar ve görselli bir gönderi yayımlamaz.",
    previewRetry: 'Yeniden dene',
    postHeading: "LinkedIn'de gönderi olarak yayımla",
    postNote:
      "Yukarıdaki görsel ve metin, kendi LinkedIn hesabınızda gönderi olarak yayımlanır. Metni burada düzenleyebilirsiniz; paylaşım yalnız «Onayla ve LinkedIn'de Yayımla» düğmesine bastığınızda gerçekleşir.",
    connect: 'LinkedIn hesabını bağla',
    connecting: 'LinkedIn penceresi bekleniyor…',
    connectedAs: 'Şu hesap adına paylaşılacak:',
    imageLabel: 'Gönderide yer alacak görsel',
    editHint: 'Bu metni paylaşmadan önce değiştirebilirsiniz.',
    connectNeeded:
      'Paylaşım için yukarıdaki butonu tıklayarak LinkedIn hesabınızla bağlantı kurmanız gerekmektedir.',
    finalTextNote: "Doğrulama bağlantısı metnin sonuna eklenir; LinkedIn'e gidecek hâli budur.",
    finalTextLabel: "LinkedIn'e gidecek metin",
    linkHint: 'Doğrulama bağlantısı, yayımlarken metnin sonuna eklenir.',
    textEmpty: 'Paylaşım metni boş olamaz.',
    textTooLong: 'Paylaşım metni çok uzun: doğrulama bağlantısıyla birlikte en fazla 3000 karakter olabilir.',
    textControl: 'Paylaşım metni yayımlanamayan bir karakter içeriyor.',
    publicNotice: "Bu gönderi LinkedIn'de herkese açık yayımlanacak.",
    publish: "Onayla ve LinkedIn'de Yayımla",
    publishing: 'Yayımlanıyor…',
    published: 'Gönderi yayımlandı.',
    viewPost: 'Gönderiyi aç ↗',
    errDenied: 'LinkedIn izni verilmedi. Paylaşmak için hesabınızı bağlamanız gerekir.',
    errPopup: 'Tarayıcı LinkedIn penceresini engelledi. Engeli kaldırıp yeniden deneyin.',
    errExpired: 'LinkedIn oturumunun süresi doldu. Hesabınızı yeniden bağlayın.',
    errStartRefused:
      'LinkedIn bağlantısı başlatılamadı: istek güvenlik denetiminden geçmedi. Hiçbir gönderi ' +
      'paylaşılmadı. Açılan pencereyi kapatıp yeniden deneyin.',
    /* Bir suçlama değil: dönüş doğrulanamadı, hepsi bu. Çerez, kimsenin seçmediği sebeplerle de
       eksik olabilir. Kesin olan tek şey hiçbir hesabın bağlanmadığıdır. */
    errBindingUnverified:
      'LinkedIn dönüşü, bağlantıyı başlatan pencereye bağlanamadı; hiçbir hesap bağlanmadı. Paylaşımı açtığınız sekmeden yeniden deneyin.',
    /* Bir ret değil: yalnız bekleme süresi doldu. Kullanıcıya söylenen de bu. */
    errTimedOut: 'LinkedIn bağlantısı beklenirken süre doldu. Bir yanıt gelmedi; yeniden deneyebilirsiniz.',
    errUnknown:
      'Sonuç bilinmiyor: gönderi oluşmuş olabilir. LinkedIn profilinizi kontrol edin; otomatik olarak yeniden denenmedi.',
    /* «Oluşmadı» kesin bir bilgidir ve «bilinmiyor»un tam tersidir; ikisi aynı kutuya girmez. */
    errNotStarted:
      "Paylaşım şu anda başlatılamadı; LinkedIn'e hiçbir gönderi gönderilmedi. Birkaç saniye sonra yeniden deneyin.",
    errFailed: 'Gönderi oluşturulamadı.',
    close: 'Kapat',
  },
  en: {
    title: 'Share as a LinkedIn post',
    note: 'This announces the badge or certificate image in your LinkedIn feed; it does not add an entry to the Licenses & certifications section of your profile.',
    noPrefill: 'LinkedIn does not pre-fill the post text; copy the text and paste it into the share box.',
    orgNote:
      "Sharing from a company page depends on the LinkedIn page admin's permissions; this system does not post to company pages automatically.",
    blocked: 'Your browser blocked the share window. Use the options below.',
    open: 'Share only the link on LinkedIn ↗',
    suggested: 'Suggested post text',
    copyText: 'Copy text',
    copyLink: 'Copy link',
    png: 'Download PNG',
    preview: 'Link preview (OG image)',
    previewNote:
      'The LinkedIn link card uses this image and title; in production the public route renders this metadata server-side in the first HTML response.',
    previewLoading: 'Loading the share image…',
    previewFailed: 'The share image could not be loaded.',
    statusUnavailable:
      'The LinkedIn publishing status could not be read just now; that does not mean the integration is off. Please try again.',
    statusRetry: 'Check the status again',
    linkAlternative: 'You can share just the link instead — that does not publish a post with the image.',
    linkOnlyDeployment:
      'Publishing an image post directly on LinkedIn is not switched on for this deployment. The link share below opens LinkedIn’s own window and does not publish a post with the image.',
    previewRetry: 'Try again',
    postHeading: 'Publish as a LinkedIn post',
    postNote:
      'The image and text above are published as a post on your own LinkedIn account. You can edit the text here; nothing is shared until you press “Confirm and publish on LinkedIn”.',
    connect: 'Connect your LinkedIn account',
    connecting: 'Waiting for the LinkedIn window…',
    connectedAs: 'Will be posted as:',
    imageLabel: 'The image your post will carry',
    editHint: 'You can change this text before you share it.',
    connectNeeded: 'To share, connect your LinkedIn account using the button above.',
    finalTextNote: 'The verification link is added to the end; this is the text LinkedIn will receive.',
    finalTextLabel: 'The text LinkedIn will receive',
    linkHint: 'The verification link is added to the end of the text when it is published.',
    textEmpty: 'The post text cannot be empty.',
    textTooLong: 'The post text is too long: with the verification link it can be at most 3000 characters.',
    textControl: 'The post text contains a character that cannot be published.',
    publicNotice: 'This post will be published publicly on LinkedIn.',
    publish: 'Confirm and publish on LinkedIn',
    publishing: 'Publishing…',
    published: 'The post was published.',
    viewPost: 'Open the post ↗',
    errDenied: 'LinkedIn permission was not granted. Connect your account to publish.',
    errPopup: 'Your browser blocked the LinkedIn window. Allow it and try again.',
    errExpired: 'The LinkedIn session expired. Connect your account again.',
    errStartRefused:
      'The LinkedIn connection could not be started: the request did not pass the security check. ' +
      'Nothing was shared. Close the window that opened and try again.',
    errBindingUnverified:
      'The return from LinkedIn could not be tied back to the window that started it; no account was connected. Try again from the tab you opened sharing in.',
    errTimedOut: 'The wait for LinkedIn ran out. No answer arrived either way; you can try again.',
    errUnknown:
      'The result is unknown: the post may exist. Check your LinkedIn profile; nothing was retried automatically.',
    errNotStarted: 'Sharing could not be started; nothing was sent to LinkedIn. Try again in a few seconds.',
    errFailed: 'The post could not be created.',
    close: 'Close',
  },
};

/**
 * The post the dialog offers, before the member edits a word of it.
 *
 * Every part comes from the RECORD: the issuing organisation's own display name, the certificate's
 * own title and — appended by the caller — the record's own public verification address. Nothing
 * is hard-coded for one certificate, and the word «credential» is gone: in Turkish the document is
 * a sertifika, a rozet, a diploma or a belge, and which one it is comes from the title's own ending
 * (`document-noun.ts`), not from a guess.
 *
 * The noun is lowered because it is a common noun inside a sentence — «… CXO 100 Seçici Kurul
 * Üyesi sertifikasını almaktan mutluluk duyuyorum» — while the record's own name keeps its
 * capitals. If the title omits the document noun, the record's type supplies it; membership
 * titles such as «CXO 100 Seçici Kurul Üyesi» still describe a certificate being received.
 *
 * An organization record speaks about the company, not in its voice.
 */
export function suggestedShareText(c: DemoCredential, lang: PublicLanguage = 'tr'): string {
  const title = withoutRegisteredMark(credTitle(c, lang));
  const issuer = withoutRegisteredMark(c.release.issuerProfileSnapshot.displayName);
  const type = credType(c);
  if (lang === 'en') {
    return type === 'organization_certificate'
      ? `${displayName(c)} has been awarded the ${title} by ${issuer}.`
      : `Happy to have received my ${title} from ${issuer}.`;
  }
  const documentTitle = titleWithDocumentNoun(title, type);
  if (type === 'organization_certificate') {
    return `${displayName(c)}, ${accusativeTitleLower(documentTitle)} almaya hak kazandı.`;
  }
  return `${issuer}'ten ${accusativeTitleLower(documentTitle)} almaktan mutluluk duyuyorum.`;
}
